import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import RecipeCard from '../components/RecipeCard';
import RecipeMenuGroup from '../components/RecipeMenuGroup';
import RecipeModal from '../components/RecipeModal';
import MenuCompletionBanner from '../components/MenuCompletionBanner';
import PlanDaysPreview from '../components/PlanDaysPreview';
import AlakartCountdownCard from '../components/AlakartCountdownCard';
import { hydrateRecipeForModal } from '../utils/recipeModalData';
import { API_BASE_URL } from '../config';
import { fetchProfessionalSummary } from '../utils/professionalSummary';
import useMenuCompletionState from '../hooks/useMenuCompletionState';
import { resolveClientMealSlot, mealSlotLabel } from '../utils/mealSlotUi';
import axios from 'axios';
import Swal from 'sweetalert2';

export default function TodayMenuPage({ isDarkMode, toggleDarkMode }) {
    const navigate = useNavigate();
    const currentMealSlot = resolveClientMealSlot();
    const currentMealLabel = mealSlotLabel(currentMealSlot);
    const [loading, setLoading] = useState(() => !localStorage.getItem('cached_menu_suggestions'));
    const [recipes, setRecipes] = useState(() => {
        const cached = localStorage.getItem('cached_menu_suggestions');
        // Cache buster: If ingredients are not structured objects, clear cache once to force update
        if (cached) {
            const parsed = JSON.parse(cached);
            const firstRecipe = Array.isArray(parsed) ? parsed[0] : parsed;
            const firstIng = firstRecipe?.ingredients?.[0];
            if (firstIng && typeof firstIng === 'string') {
                console.log("🧹 Structure mismatch detected in cache, clearing for sync...");
                localStorage.removeItem('cached_menu_suggestions');
                return [];
            }
            return Array.isArray(parsed) ? parsed : [];
        }
        return [];
    });
    const [structuredMenu, setStructuredMenu] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [planningConfig, setPlanningConfig] = useState(null);
    const [planAuthority, setPlanAuthority] = useState(null);
    const [planWeekDays, setPlanWeekDays] = useState([]); // diet/athlete bugünün öğünleri (PlanDaysPreview)
    const [selectedPortion, setSelectedPortion] = useState(null);
    const [isEmptyHanded, setIsEmptyHanded] = useState(false);
    const [mealSlotDisabled, setMealSlotDisabled] = useState(false);
    const [gateState, setGateState] = useState('');
    const [mealSlotActivation, setMealSlotActivation] = useState(null);
    const [mealSlotTrigger, setMealSlotTrigger] = useState(null);
    const [recentWarmup, setRecentWarmup] = useState(null);
    const [mealIntentPush, setMealIntentPush] = useState(null);
    const [professionalSummary, setProfessionalSummary] = useState({
        isProfessional: false,
        professionalType: null,
        panelPath: null,
        activeClientCount: 0,
        activeClients: [],
    });
    const [isAdminUser, setIsAdminUser] = useState(false);

    // Modal State
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [recipeModalOpen, setRecipeModalOpen] = useState(false);
    const [selectedRecipeNames, setSelectedRecipeNames] = useState([]);
    const { isCurrentMealCompleted } = useMenuCompletionState({ mealSlot: currentMealSlot });
    const warmupStatusLabels = {
        queued: 'Sıraya alındı',
        running: 'Hazırlanıyor',
        completed: 'Hazırlandı',
        coalesced: 'Birleşti',
        locked: 'Kilitli',
        no_eligible_jobs: 'Uygun iş yok',
        failed: 'Başarısız',
        disabled: 'Kapalı'
    };
    const warmupStatusLabelsClean = {
        queued: 'Sıraya alındı',
        running: 'Hazırlanıyor',
        completed: 'Hazırlandı',
        coalesced: 'Birleşti',
        locked: 'Kilitli',
        no_eligible_jobs: 'Uygun iş yok',
        failed: 'Başarısız',
        disabled: 'Kapalı'
    };
    const mealIntentStatusLabels = {
        eligible: 'Hazır',
        waiting_window: 'Pencere bekleniyor',
        waiting_snapshot: 'Snapshot bekleniyor',
        gate_blocked: 'Gate bloklu',
        consumed: 'Tamamlandı',
        slot_disabled: 'Slot kapalı',
        unsupported_slot: 'Desteklenmiyor'
    };
    const warmupStatus = String(recentWarmup?.status || '').trim().toLowerCase();
    const warmupLabel = warmupStatusLabelsClean[warmupStatus] || 'Bekleniyor';
    const mealIntentStatus = String(mealIntentPush?.status || '').trim().toLowerCase();
    const mealIntentLabel = mealIntentStatusLabels[mealIntentStatus] || 'Bekleniyor';
    const triggerInfoVisible = Boolean(mealSlotTrigger?.slot || recentWarmup?.status || mealIntentPush?.status);
    const triggerToneClass = warmupStatus === 'completed'
        ? 'border-emerald-200 bg-emerald-50/90 text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-900/20 dark:text-emerald-300'
        : warmupStatus === 'running'
            ? 'border-sky-200 bg-sky-50/90 text-sky-700 dark:border-sky-800/60 dark:bg-sky-900/20 dark:text-sky-300'
            : warmupStatus === 'queued'
                ? 'border-amber-200 bg-amber-50/90 text-amber-700 dark:border-amber-800/60 dark:bg-amber-900/20 dark:text-amber-300'
                : 'border-slate-200 bg-white/80 text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300';

    const mapPlanningStructureToSmartCategories = () => ['main', 'soup', 'salad'];

    useEffect(() => {
        const initialize = async () => {
            try {
                const token = localStorage.getItem('authToken');

                // 1. Fetch Profile, Planning Config, and the date-aware plan authority
                const [profileRes, configRes, resolveRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/profile`, { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get(`${API_BASE_URL}/api/planning/config`, { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get(`${API_BASE_URL}/api/planning/today-resolve`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
                ]);

                if (resolveRes?.data?.success) {
                    setPlanAuthority(resolveRes.data);
                }

                const profilePayload = profileRes?.data || {};
                const adminDetected = Boolean(
                    profilePayload?.user?.isAdmin ||
                    profilePayload?.profile?.user?.isAdmin ||
                    profilePayload?.isAdmin
                );
                setIsAdminUser(adminDetected);

                if (adminDetected) {
                    setProfessionalSummary({
                        isProfessional: false,
                        professionalType: null,
                        panelPath: null,
                        activeClientCount: 0,
                        activeClients: [],
                    });
                } else {
                    const summary = await fetchProfessionalSummary(token);
                    setProfessionalSummary(summary);
                }

                setUserProfile(profileRes.data.profile);
                setPlanningConfig(configRes.data.config);

                // 1.5 [Yetki Mimarisi] Aktif plan bugünü kapsıyorsa, planın GERÇEK öğünlerini göm.
                // Smart-search yalnızca plan kapsamayan günde (source=smart) devreye girer.
                const auth = resolveRes?.data?.success ? resolveRes.data : null;
                const authSrc = auth?.source;
                const authCovered = Boolean(auth?.covered) && ['diet', 'athlete', 'alakart'].includes(authSrc);
                if (authCovered) {
                    try {
                        if (authSrc === 'diet' || authSrc === 'athlete') {
                            const wpRes = await axios.get(`${API_BASE_URL}/api/${authSrc}/weekly-plan`, {
                                headers: { Authorization: `Bearer ${token}` },
                                params: { horizon: 'today' }
                            });
                            const wp = Array.isArray(wpRes.data?.weekPlan) ? wpRes.data.weekPlan : [];
                            const todayDay = wp.find((d) => d.date === auth.today) || wp[0] || null;
                            if (todayDay && todayDay.meals) {
                                setPlanWeekDays([todayDay]);
                                setIsEmptyHanded(false);
                                return; // smart-search'i atla (finally setLoading(false) yapar)
                            }
                        } else if (authSrc === 'alakart') {
                            const plRes = await axios.get(`${API_BASE_URL}/api/alakart-menu/plan`, {
                                headers: { Authorization: `Bearer ${token}` }
                            });
                            const days = plRes.data?.plan?.effectiveDays || plRes.data?.plan?.generatedDays || {};
                            const dayObj = days[auth.today] || null;
                            if (dayObj && typeof dayObj === 'object') {
                                // Slot-bazlı menüyü (SOUP/MAIN/SALAD…) düz tarif dizisine çevir.
                                const slotOrder = ['SOUP', 'MAIN', 'CHICKEN', 'MEAT', 'FISH', 'LEGUME', 'VEGETABLE', 'PASTA', 'SALAD', 'DESSERT'];
                                const entries = Object.entries(dayObj).filter(([k, v]) => k !== '_meta' && v && typeof v === 'object' && (v.id || v.recipeId));
                                entries.sort((a, b) => {
                                    const ia = slotOrder.indexOf(a[0].toUpperCase()); const ib = slotOrder.indexOf(b[0].toUpperCase());
                                    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
                                });
                                const dishes = entries.map(([, v]) => v);
                                if (dishes.length > 0) {
                                    setStructuredMenu(null);
                                    setRecipes(dishes);
                                    setIsEmptyHanded(false);
                                    return; // mevcut recipes render yolu çizer
                                }
                            }
                        }
                    } catch (embedErr) {
                        console.warn('[TodayMenu] aktif plan gömme atlandı, smart-search devralıyor:', embedErr?.message);
                        // embed başarısız → smart-search'e düş
                    }
                }

                // 1.6 [Yetki Mimarisi] Alerjik premium kullanıcının BASE kaynağı = ALERJEN mutfağı (smart yerine).
                // Plan (alakart/diet/athlete) yoksa ve kullanıcı alerjik+premium ise günün menüsü alerjen-safe üretilir.
                if (auth?.source === 'allergen') {
                    try {
                        const alRes = await axios.get(`${API_BASE_URL}/api/allergen-kitchen/preload`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        const sm = alRes.data?.initialData?.structuredMenu || null;
                        const recs = Array.isArray(alRes.data?.initialData?.recipes) ? alRes.data.initialData.recipes : [];
                        if (sm) {
                            setStructuredMenu(sm);
                            const flat = [
                                sm.soup,
                                sm.main ? { ...sm.main, suggestedPairings: Array.isArray(sm.pair) ? sm.pair.filter(Boolean) : [] } : null,
                                sm.salad,
                                sm.dessert
                            ].filter(Boolean);
                            setRecipes(flat);
                            setIsEmptyHanded(false);
                            return;
                        }
                        if (recs.length > 0) {
                            setStructuredMenu(null);
                            setRecipes(recs);
                            setIsEmptyHanded(false);
                            return;
                        }
                        // alerjen menü boşsa smart-search devralır (premium kapısı açık ama uygun yemek yoksa)
                    } catch (alErr) {
                        console.warn('[TodayMenu] alerjen menü gömme atlandı, smart-search devralıyor:', alErr?.message);
                    }
                }

                // 2. Use Smart Search preload as single source of truth
                // so Today Menu and Smart Search show the same snapshot.
                const preloadRes = await axios.get(`${API_BASE_URL}/api/smart-search/preload?mealSlot=${encodeURIComponent(currentMealSlot)}&intentSource=today_menu_preload`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const preloadData = preloadRes?.data || {};
                const defaults = preloadData?.defaults || {};
                const initialData = preloadData?.initialData || {};
                const preloadState = String(initialData?.meta?.state || preloadData?.meta?.state || '');
                setGateState(preloadState);
                setMealSlotActivation(preloadData?.mealSlotActivation || configRes?.data?.config?.mealSlotActivation || null);
                setMealSlotTrigger(preloadData?.mealSlotTrigger || null);
                setRecentWarmup(preloadData?.recentWarmup || null);
                setMealIntentPush(preloadData?.mealIntentPush || null);

                let suggestRes = { data: { success: false } };
                const hasInitialStructured = Boolean(initialData?.structuredMenu);
                const hasInitialRecipes = Array.isArray(initialData?.recipes) && initialData.recipes.length > 0;

                if (hasInitialStructured || hasInitialRecipes) {
                    suggestRes = {
                        data: {
                            success: true,
                            recipes: initialData.recipes || [],
                            structuredMenu: initialData.structuredMenu || null,
                            availableComponents: initialData.availableComponents || [],
                            isRotationSkipped: false
                        }
                    };
                } else if (preloadState === 'meal_slot_disabled') {
                    suggestRes = {
                        data: {
                            success: true,
                            recipes: [],
                            structuredMenu: null,
                            availableComponents: [],
                            meta: initialData?.meta || preloadData?.meta || {}
                        }
                    };
                } else {
                    const preferredCategories = Array.isArray(defaults?.selectedCategories) && defaults.selectedCategories.length > 0
                        ? defaults.selectedCategories
                        : mapPlanningStructureToSmartCategories();
                    const servingSize = Number(defaults?.portion || profileRes?.data?.profile?.servingSize || 4) || 4;
                    const skillLevel = defaults?.skillLevel || 'Acemi';
                    suggestRes = await axios.post(`${API_BASE_URL}/api/smart-search/recommendations`, {
                        preferredCategories,
                        servingSize,
                        skillLevel,
                        mealSlot: currentMealSlot,
                        intentSource: 'today_menu'
                    }, { headers: { Authorization: `Bearer ${token}` } });
                }

                if (suggestRes.data.success) {
                    const slotDisabled = String(suggestRes.data?.meta?.state || '') === 'meal_slot_disabled';
                    setGateState(String(suggestRes.data?.meta?.state || preloadState || ''));
                    setMealSlotDisabled(slotDisabled);
                    setMealSlotTrigger(suggestRes.data?.mealSlotTrigger || preloadData?.mealSlotTrigger || null);
                    setRecentWarmup(suggestRes.data?.recentWarmup || preloadData?.recentWarmup || null);
                    setMealIntentPush(suggestRes.data?.mealIntentPush || preloadData?.mealIntentPush || null);
                    const nextStructuredMenu = suggestRes.data.structuredMenu || null;
                    setStructuredMenu(nextStructuredMenu);

                    const nextRecipes = nextStructuredMenu
                        ? [
                            nextStructuredMenu.soup,
                            nextStructuredMenu.main
                                ? {
                                    ...nextStructuredMenu.main,
                                    suggestedPairings: Array.isArray(nextStructuredMenu.pair) ? nextStructuredMenu.pair.filter(Boolean) : []
                                }
                                : null,
                            nextStructuredMenu.salad,
                            nextStructuredMenu.dessert
                        ].filter(Boolean)
                        : (suggestRes.data.recipes || []);
                    if (nextRecipes.length === 0) {
                        setIsEmptyHanded(true);
                        setRecipes([]);
                        localStorage.removeItem('cached_menu_suggestions');
                    } else {
                        setRecipes(nextRecipes);
                        localStorage.setItem('cached_menu_suggestions', JSON.stringify(nextRecipes));
                        setIsEmptyHanded(false);
                    }

                    // Show Rotation Skipped (Economy/SKT) Modal if flag is true
                    if (suggestRes.data.isRotationSkipped) {
                        Swal.fire({
                            title: 'Tasarruf ve Sağlık Modu 💡',
                            text: 'Şefim; mutfağınızı tasarruf modunda çalıştırdığımız, bunu yaparken sizin ve ailenizin sağlık dengesini de tutmaya çalıştığımız için önerilerimiz bir önceki gün ile aynı olabilir. Eğer sistemimiz içerisinde bulunan tarifleri uyguluyor yada kullanıyorsanız lütfen tariflerin altında bulunan kullandım butonuna basarak mutfağınızı güncelleyin. Böylece hergün yeni lezzetlerle buluşurken mutfak ekonominizide korumuş olacağız.',
                            icon: 'info',
                            confirmButtonText: 'Anladım',
                            confirmButtonColor: '#10b981'
                        });
                    }
                }
            } catch (error) {
                console.error('Failed to load today menu:', error);
                // Don't show global error if we already have cached data
                if (recipes.length === 0) {
                    Swal.fire('Hata', 'Menü yüklenirken bir sorun oluştu.', 'error');
                }
            } finally {
                setLoading(false);
            }
        };

        initialize();
    }, []);

    const handleOpenRecipe = async (recipe) => {
        // Plan öğünlerinde composer porsiyonu (mealPortion) otoritatif — modal malzeme+fiyatı onunla ölçeklesin.
        const mp = Number(recipe?.mealPortion);
        const portion = Number.isFinite(mp) && mp > 0 ? mp : null;
        setSelectedPortion(portion);
        setSelectedRecipe(await hydrateRecipeForModal(recipe, portion ? { targetPortion: portion } : {}));
        setRecipeModalOpen(true);
    };

    const toggleSelection = (recipeOrRecipes) => {
        const recipesToToggle = Array.isArray(recipeOrRecipes) ? recipeOrRecipes : [recipeOrRecipes];
        const namesToToggle = recipesToToggle.map((r) => r.name || r.title);

        let next = [...selectedRecipeNames];
        const allSelected = namesToToggle.every((name) => next.includes(name));

        if (allSelected) {
            next = next.filter((name) => !namesToToggle.includes(name));
        } else {
            namesToToggle.forEach((name) => {
                if (!next.includes(name)) next.push(name);
            });
        }
        setSelectedRecipeNames(next);
    };

    // Plan authority (date-aware): which kitchen owns today's menu.
    const AUTHORITY_META = {
        alakart: { label: 'Alakart Menü', route: '/alakart-menu', icon: 'calendar_month', cta: 'Alakart planının bugünkü menüsünü gör' },
        diet: { label: 'Diyet Mutfağı', route: '/diet-kitchen', icon: 'monitor_heart', cta: 'Diyet planının bugünkü menüsünü gör' },
        athlete: { label: 'Sporcu Mutfağı', route: '/athlete-kitchen', icon: 'fitness_center', cta: 'Sporcu planının bugünkü menüsünü gör' },
        allergen: { label: 'Alerjen Mutfağı', route: '/allergen-kitchen', icon: 'health_and_safety', cta: 'Alerjen mutfağına git' },
    };
    const authoritySource = planAuthority?.source;
    const authorityCovered = Boolean(planAuthority?.covered) && Boolean(AUTHORITY_META[authoritySource]);
    const authorityMeta = authorityCovered ? AUTHORITY_META[authoritySource] : null;
    const authorityNotice = planAuthority?.notice || null;
    const isAlakartUpcoming = authorityNotice?.type === 'upcoming_plan' && authorityNotice?.planSource === 'alakart';

    return (
        <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
            <div className="max-w-6xl mx-auto px-6 py-8">
                {/* Header */}
                <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
                    <div className="flex items-center gap-5">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="p-3 rounded-2xl bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-primary hover:text-white transition-all shadow-sm border border-slate-100 dark:border-slate-700"
                        >
                            <span className="material-symbols-outlined font-bold">arrow_back</span>
                        </button>
                        <div>
                            <div className="flex items-center gap-3 mb-1">
                                <span className="material-symbols-outlined text-primary text-3xl">restaurant_menu</span>
                                <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">Günün Menüsü</h1>
                            </div>
                            <p className="text-slate-500 dark:text-slate-400 font-medium">Mutfağınızdaki malzemelere ve tercihlerine göre şefin seçtikleri.</p>
                        </div>
                    </div>
                    {professionalSummary.isProfessional && !isAdminUser && (
                        <div className="rounded-2xl border border-cyan-200 bg-cyan-50/90 px-5 py-4 dark:border-cyan-900/40 dark:bg-cyan-900/10">
                            <div className="text-xs font-bold uppercase tracking-wide text-cyan-700 dark:text-cyan-300">
                                Danışan Takibi
                            </div>
                            <div className="mt-1 text-lg font-black text-cyan-900 dark:text-cyan-100">
                                {professionalSummary.activeClientCount} aktif danışan
                            </div>
                            <button
                                onClick={() => navigate(professionalSummary.panelPath || '/diyetisyen-panel')}
                                className="mt-2 text-xs font-bold text-cyan-700 underline underline-offset-2 hover:text-cyan-900 dark:text-cyan-300 dark:hover:text-cyan-100"
                            >
                                Danışan paneline git
                            </button>
                        </div>
                    )}
                </header>

                {/* Alakart geri sayım + alışveriş durumu kartı (yaklaşan plan) */}
                {isAlakartUpcoming && (
                    <div className="mb-6">
                        <AlakartCountdownCard
                            notice={authorityNotice}
                            onGoShopping={(listId) => navigate(listId ? `/my-shopping-lists?list=${listId}` : '/my-shopping-lists')}
                            onGoPlan={() => navigate('/alakart-menu')}
                        />
                    </div>
                )}

                {/* Plan authority banner — which kitchen owns today's menu (date-aware) */}
                {authorityCovered && (
                    <div className="mb-6 rounded-2xl border border-primary/30 bg-primary/5 dark:border-primary/40 dark:bg-primary/10 px-5 py-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div className="flex items-center gap-3">
                                <span className="material-symbols-outlined text-primary text-2xl">{authorityMeta.icon}</span>
                                <div>
                                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-primary">Aktif Plan</p>
                                    <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                                        Bugünün menüsü <span className="text-primary">{authorityMeta.label}</span> planından geliyor.
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate(authorityMeta.route)}
                                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:brightness-110"
                            >
                                {authorityMeta.cta}
                                <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
                            </button>
                        </div>
                        {authorityNotice?.message && !isAlakartUpcoming && (
                            <p className="mt-3 flex items-start gap-2 text-xs font-semibold text-amber-700 dark:text-amber-300">
                                <span className="material-symbols-outlined text-[16px] mt-0.5">info</span>
                                {authorityNotice.message}
                            </p>
                        )}
                    </div>
                )}

                {/* No active plan today — smart-search fills the gap, but inform the user */}
                {!authorityCovered && authorityNotice?.message && !isAlakartUpcoming && (
                    <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50/90 dark:border-amber-900/40 dark:bg-amber-900/10 px-5 py-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <p className="flex items-start gap-2 text-sm font-semibold text-amber-800 dark:text-amber-200">
                                <span className="material-symbols-outlined text-[18px] mt-0.5">info</span>
                                {authorityNotice.message}
                            </p>
                            {authorityNotice.planSource && AUTHORITY_META[authorityNotice.planSource] && (
                                <button
                                    onClick={() => navigate(AUTHORITY_META[authorityNotice.planSource].route)}
                                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-amber-300 bg-white/70 px-4 py-2 text-xs font-bold text-amber-800 transition-all hover:bg-white dark:border-amber-800/50 dark:bg-amber-900/20 dark:text-amber-200"
                                >
                                    {AUTHORITY_META[authorityNotice.planSource].label}
                                    <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {triggerInfoVisible && (
                    <div className={`mb-8 rounded-2xl border px-4 py-3 ${triggerToneClass}`}>
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-[18px]">schedule</span>
                                <p className="text-xs font-black uppercase tracking-[0.2em]">Öğün Trigger Durumu</p>
                            </div>
                            <div className="text-sm font-bold">
                                {currentMealLabel} • Warmup: {warmupLabel} • Push: {mealIntentLabel}
                            </div>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold">
                            {mealSlotTrigger?.scheduledTime && (
                                <span className="rounded-full bg-black/5 px-3 py-1 dark:bg-white/10">
                                    Slot saati: {mealSlotTrigger.scheduledTime}
                                </span>
                            )}
                            {mealSlotTrigger?.preTriggerTime && (
                                <span className="rounded-full bg-black/5 px-3 py-1 dark:bg-white/10">
                                    Pre-trigger: {mealSlotTrigger.preTriggerTime}
                                </span>
                            )}
                            {mealSlotTrigger?.phase && (
                                <span className="rounded-full bg-black/5 px-3 py-1 dark:bg-white/10">
                                    Faz: {mealSlotTrigger.phase}
                                </span>
                            )}
                            {mealIntentPush?.status && (
                                <span className="rounded-full bg-black/5 px-3 py-1 dark:bg-white/10">
                                    Push intent: {mealIntentLabel}
                                </span>
                            )}
                            {mealIntentPush?.reason && (
                                <span className="rounded-full bg-black/5 px-3 py-1 dark:bg-white/10">
                                    Neden: {mealIntentPush.reason}
                                </span>
                            )}
                            {recentWarmup?.updatedAt && (
                                <span className="rounded-full bg-black/5 px-3 py-1 dark:bg-white/10">
                                    Son güncelleme: {new Date(recentWarmup.updatedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                        <p className="text-slate-500 font-bold animate-pulse">Menünüz Hazırlanıyor...</p>
                    </div>
                ) : planWeekDays.length > 0 ? (
                    <PlanDaysPreview
                        days={planWeekDays}
                        isDarkMode={isDarkMode}
                        onRecipeClick={handleOpenRecipe}
                        showTrainingBadges={authoritySource === 'athlete'}
                        emptyMessage="Bugün için plan menüsü hazırlanamadı."
                    />
                ) : mealSlotDisabled ? (
                    <div className="bg-white dark:bg-slate-800 rounded-3xl p-12 text-center border border-sky-200 dark:border-sky-800">
                        <div className="w-24 h-24 bg-sky-50 dark:bg-sky-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="material-symbols-outlined text-sky-500 text-5xl">schedule</span>
                        </div>
                        <h3 className="text-2xl font-black text-slate-800 dark:text-white mb-3">{currentMealLabel} öğünü şu anda kapalı</h3>
                        <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-3 font-medium">
                            Ankette bu öğün için “yapmıyorum” seçildiği için bu saatte menü üretilmiyor.
                        </p>
                        {mealSlotActivation?.[currentMealSlot]?.time && (
                            <p className="text-sm font-bold text-slate-600 dark:text-slate-300 mb-8">
                                Kayıtlı slot saati: {mealSlotActivation[currentMealSlot].time}
                            </p>
                        )}
                        <button
                            onClick={() => navigate('/profile')}
                            className="px-8 py-3 bg-sky-600 text-white rounded-2xl font-black shadow-lg shadow-sky-500/20 hover:scale-105 transition-all"
                        >
                            Profilden Düzenle
                        </button>
                    </div>
                ) : gateState === 'breakfast_separate_domain' ? (
                    <div className="py-12">
                        <div className="max-w-3xl mx-auto">
                            <MenuCompletionBanner
                                isDarkMode={isDarkMode}
                                title="Şefim, afiyet olsun"
                                subtitle="Kahvaltı önerileri ayrı akışta hazırlanıyor."
                                detail="Sabah önerilerini kahvaltı ekranından görebilirsin. Öğle ve akşam için burada yeniden öneri alabilirsin."
                            />
                        </div>
                    </div>
                ) : isCurrentMealCompleted && recipes.length === 0 ? (
                    <div className="py-12">
                        <div className="max-w-3xl mx-auto">
                            <MenuCompletionBanner
                                isDarkMode={isDarkMode}
                                subtitle={`${currentMealLabel} öğününü tamamladık.`}
                                detail="Diğer öğünler bildirim yapılmasını istediğin saatte hazırlanacak."
                            />
                        </div>
                    </div>
                ) : isEmptyHanded ? (
                    <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
                        <div className="size-24 rounded-[2rem] bg-amber-50 dark:bg-amber-900/20 text-amber-500 flex items-center justify-center mb-8 border border-amber-100 dark:border-amber-800 animate-bounce">
                            <span className="material-symbols-outlined text-5xl">inventory_2</span>
                        </div>
                        <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-3">Bugün için bir menü oluşturamadık şefim.</h2>
                        <p className="text-slate-500 dark:text-slate-400 max-w-md font-medium leading-relaxed">
                            Mevcut malzemelerinizle kusursuz bir menü hazırlamak için henüz biraz eksiğimiz var. Dolabınıza biraz daha malzeme eklemeye ne dersiniz?
                        </p>
                        <button
                            onClick={() => navigate('/my-shopping-lists')}
                            className="mt-8 px-8 py-4 bg-primary text-white rounded-2xl font-black shadow-lg shadow-primary/25 hover:scale-105 active:scale-95 transition-all"
                        >
                            Hemen Alışveriş Yap
                        </button>
                    </div>
                ) : recipes.length > 0 ? (
                    <>
                        {isCurrentMealCompleted && (
                            <div className="mb-6">
                                <MenuCompletionBanner
                                    isDarkMode={isDarkMode}
                                    subtitle={`${currentMealLabel} öğününü tamamladık.`}
                                    detail="Diğer öğünler bildirim yapılmasını istediğin saatte hazırlanacak."
                                />
                            </div>
                        )}
                        <div className={`grid grid-cols-1 md:grid-cols-2 ${Array.isArray(structuredMenu?.pair) && structuredMenu.pair.filter(Boolean).length > 0 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6`}>
                        {/* Lunchbox Message if any */}
                        {recipes.find(r => r.lunchboxMessage) && (
                            <div className="col-span-full mb-4 p-5 rounded-[2rem] bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-xl border border-blue-400/50 flex items-center gap-6 animate-in slide-in-from-top-4 duration-500">
                                <div className="size-16 rounded-2xl bg-white/20 backdrop-blur-xl flex items-center justify-center shrink-0 border border-white/30 shadow-inner">
                                    <span className="material-symbols-outlined text-[32px] text-white">backpack</span>
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs font-black uppercase tracking-[0.2em] opacity-80 mb-1 text-blue-100">Beslenme Çantası Önerisi</p>
                                    <p className="text-lg font-bold leading-tight drop-shadow-sm">{recipes.find(r => r.lunchboxMessage).lunchboxMessage}</p>
                                </div>
                                <div className="text-4xl pr-2 grayscale-0 drop-shadow-lg">🍱</div>
                            </div>
                        )}

                        {structuredMenu ? (() => {
                            const currentPortion = planningConfig?.[new Date().getHours() < 18 ? 'lunch' : 'dinner']?.servings || 4;
                            const pairings = Array.isArray(structuredMenu.pair) ? structuredMenu.pair.filter(Boolean) : [];
                            const hasPairings = pairings.length > 0;
                            const mainWithPairings = structuredMenu.main
                                ? { ...structuredMenu.main, suggestedPairings: pairings }
                                : null;
                            const slots = [
                                { id: 'soup', label: 'ÇORBA', recipe: structuredMenu.soup, layoutMode: 'compact' },
                                { id: 'main', label: hasPairings ? 'ANA YEMEK + EŞLİKÇİ' : 'ANA YEMEK', recipe: mainWithPairings, layoutMode: hasPairings ? 'featured' : 'compact' },
                                { id: 'salad', label: 'SALATA', recipe: structuredMenu.salad, layoutMode: 'compact' }
                            ].filter((slot) => slot.recipe);

                            return slots.map((slot) => (
                                <div key={slot.id} className={`${slot.id === 'main' && hasPairings ? 'lg:col-span-2' : 'lg:col-span-1'} flex flex-col h-full`}>
                                    <div className="mb-3 px-2 flex-none">
                                        <span className="px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-black rounded-lg uppercase tracking-widest border border-slate-200 dark:border-slate-700">
                                            {slot.label}
                                        </span>
                                    </div>
                                    <div className="flex-1">
                                        <RecipeMenuGroup
                                            recipe={slot.recipe}
                                            isDarkMode={isDarkMode}
                                            onCardClick={handleOpenRecipe}
                                            onToggleSelect={toggleSelection}
                                            selectedRecipeNames={selectedRecipeNames}
                                            isCompleted={isCurrentMealCompleted}
                                            userProfile={userProfile}
                                            userPortion={currentPortion}
                                            isModern={true}
                                            layoutMode={slot.layoutMode}
                                        />
                                    </div>
                                </div>
                            ));
                        })() : recipes.map((recipe, idx) => {
                            const currentPortion = planningConfig?.[new Date().getHours() < 18 ? 'lunch' : 'dinner']?.servings || 4;
                            const hasPairings = Array.isArray(recipe.suggestedPairings) && recipe.suggestedPairings.length > 0;

                            return (
                                <div key={idx} className={hasPairings ? 'lg:col-span-2' : 'lg:col-span-1'}>
                                    <RecipeMenuGroup
                                        recipe={recipe}
                                        isDarkMode={isDarkMode}
                                        onCardClick={handleOpenRecipe}
                                        onToggleSelect={toggleSelection}
                                        selectedRecipeNames={selectedRecipeNames}
                                        isCompleted={isCurrentMealCompleted}
                                        userProfile={userProfile}
                                        userPortion={currentPortion}
                                        isModern={true}
                                        layoutMode={hasPairings ? 'browseFeaturedPair' : 'compact'}
                                    />
                                </div>
                            );
                        })}
                        </div>
                    </>
                ) : (
                    <div className="bg-white dark:bg-slate-800 rounded-3xl p-12 text-center border-2 border-dashed border-slate-200 dark:border-slate-700">
                        <div className="w-24 h-24 bg-slate-50 dark:bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="material-symbols-outlined text-slate-400 text-5xl">inventory_2</span>
                        </div>
                        <h3 className="text-2xl font-black text-slate-800 dark:text-white mb-3">Malzeme Yetersiz</h3>
                        <p className="text-slate-500 dark:text-slate-400 max-w-sm mx-auto mb-8 font-medium">
                            Bugün için bir menü oluşturamadık şefim. Dolabınızda biraz daha malzeme eklemeye ne dersiniz?
                        </p>
                        <div className="flex justify-center gap-4">
                            <button
                                onClick={() => navigate('/my-kitchen')}
                                className="px-8 py-3 bg-primary text-white rounded-2xl font-black shadow-lg shadow-primary/20 hover:scale-105 transition-all"
                            >
                                MUTFAĞA GİT
                            </button>
                            <button
                                onClick={() => navigate('/quick-search')}
                                className="px-8 py-3 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-2xl font-black hover:bg-slate-200 dark:hover:bg-slate-600 transition-all"
                            >
                                AKILLI ARA
                            </button>
                        </div>
                    </div>
                )
                }
            </div>

            {/* Recipe Detail Modal */}
            {selectedRecipe && (
                <RecipeModal
                    onPairingClick={handleOpenRecipe}
                    isOpen={recipeModalOpen}
                    onClose={() => setRecipeModalOpen(false)}
                    recipe={selectedRecipe}
                    userProfile={userProfile}
                    userPortion={selectedPortion || planningConfig?.[new Date().getHours() < 18 ? 'lunch' : 'dinner']?.servings || 4}
                    skillLevel={userProfile?.skillLevel}
                    isDarkMode={isDarkMode}
                />
            )}
        </DashboardLayout>
    );
}
